/**
 * One answer-precedence function over question ids (crossmarc/steward-app#2553, prep for docs/40
 * "Toward one client profile"). For a question it returns the winning answer and where it came
 * from. The order is fixed: the client's own answer, then the staff answer, then the prefill.
 *
 * ## Tri-state per source
 *
 * A source says one of three things about a question:
 *   - `absent`: it has nothing to say. The next source down is consulted.
 *   - `empty`: an explicit empty. This IS an answer ("none", a cleared box). It wins over every
 *     lower source and is never treated as absent. A client who erased a prefilled value has
 *     answered; the prefill must not come back.
 *   - `value`: an answer with content.
 *
 * What counts as "empty" for a given storage shape is the CALLER's decision, made when it builds
 * the source answer (see `mergeOnboardingPrefill` in `onboardingForm.ts`, which reads an absent
 * key inside a saved section as an explicit empty). This module only orders the three states.
 *
 * ## Scope, stated as of 2026-10-24
 *
 * The only caller is `mergeOnboardingPrefill`, which supplies a `client` and a `prefill` source.
 * No `staff` source exists in the codebase yet (docs/40 slice A); the parameter is accepted and
 * tested so that slice adds a data source, not a second precedence rule. The `by` and `at` a
 * source attaches are passed through to the provenance untouched; nothing validates them here.
 */

export type AnswerSource = 'client' | 'staff' | 'prefill';

/** Highest priority first. The one place the order is written down. */
export const ANSWER_SOURCE_PRECEDENCE: readonly AnswerSource[] = ['client', 'staff', 'prefill'];

/** What one source says about one question. `by` and `at` ride along into the provenance. */
export type SourceAnswer<V> =
  | { state: 'absent' }
  | { state: 'empty'; by?: string; at?: string }
  | { state: 'value'; value: V; by?: string; at?: string };

export interface AnswerProvenance {
  source: AnswerSource;
  /** Who gave the answer, when the source recorded it (a staff member's id, say). */
  by?: string;
  /** When the answer was given, when the source recorded it (ISO 8601). */
  at?: string;
}

/** The winner for one question. `unanswered` means every source was absent. */
export type ResolvedAnswer<V> =
  | { state: 'unanswered' }
  | { state: 'empty'; provenance: AnswerProvenance }
  | { state: 'value'; value: V; provenance: AnswerProvenance };

/** One source's answers keyed by question id (or by item address, see `questionAnswerKey`). */
export type SourceAnswers<V> = Readonly<Record<string, SourceAnswer<V>>>;

export type AnswerSources<V> = Partial<Record<AnswerSource, SourceAnswer<V> | undefined>>;

const provenanceOf = (source: AnswerSource, a: { by?: string; at?: string }): AnswerProvenance => {
  const p: AnswerProvenance = { source };
  if (a.by !== undefined) p.by = a.by;
  if (a.at !== undefined) p.at = a.at;
  return p;
};

/** The winning answer for one question given each source's say. A missing source is `absent`. */
export function resolveAnswer<V>(sources: AnswerSources<V>): ResolvedAnswer<V> {
  for (const source of ANSWER_SOURCE_PRECEDENCE) {
    const a = sources[source];
    if (!a || a.state === 'absent') continue;
    if (a.state === 'empty') return { state: 'empty', provenance: provenanceOf(source, a) };
    return { state: 'value', value: a.value, provenance: provenanceOf(source, a) };
  }
  return { state: 'unanswered' };
}

/**
 * {@link resolveAnswer} over many questions. Returns an entry for every id that ANY source
 * mentions with a non-absent state; a question nobody answered is simply not in the result.
 */
export function resolveAnswers<V>(
  sources: Partial<Record<AnswerSource, SourceAnswers<V> | undefined>>,
): Map<string, Exclude<ResolvedAnswer<V>, { state: 'unanswered' }>> {
  const ids = new Set<string>();
  for (const source of ANSWER_SOURCE_PRECEDENCE) {
    for (const id of Object.keys(sources[source] ?? {})) ids.add(id);
  }
  const out = new Map<string, Exclude<ResolvedAnswer<V>, { state: 'unanswered' }>>();
  for (const id of ids) {
    const r = resolveAnswer<V>({
      client: sources.client?.[id], staff: sources.staff?.[id], prefill: sources.prefill?.[id],
    });
    if (r.state !== 'unanswered') out.set(id, r);
  }
  return out;
}
